import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth, type NetworkInfo } from "@/lib/auth";
import { Building2 } from "lucide-react";
import { cn } from "@/lib/utils";

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Network picker state
  const [pendingNetworks, setPendingNetworks] = useState<NetworkInfo[] | null>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [pendingRefreshToken, setPendingRefreshToken] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await api.post("/v1/auth/login", { email, password });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Login failed");
        return;
      }

      const nets: NetworkInfo[] = data.networks || [];
      const visibleNetworks = nets.filter((n) => n.networkName !== "__platform__");

      if (visibleNetworks.length > 1) {
        setPendingNetworks(visibleNetworks);
        setPendingToken(data.token);
        setPendingRefreshToken(data.refreshToken);
      } else {
        login(data.token, data.refreshToken || "", nets);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleNetworkSelect(networkId: number) {
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/v1/auth/login", { email, password, networkId });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to switch network");
        return;
      }

      login(data.token, data.refreshToken || "", data.networks || pendingNetworks || []);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Network picker screen
  if (pendingNetworks) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Choose Network</h1>
            <p className="text-gray-500 mt-2">Select which network to access</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-2">
            {error && (
              <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200 mb-2">
                {error}
              </div>
            )}

            {pendingNetworks.map((net) => (
              <button
                key={net.networkId}
                onClick={() => handleNetworkSelect(net.networkId)}
                disabled={loading}
                className="w-full flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <div className="p-2 rounded-lg bg-gray-100">
                  <Building2 className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{net.networkName}</p>
                  <p className="text-xs text-gray-500 capitalize">{net.role}</p>
                </div>
              </button>
            ))}

            <button
              onClick={() => { setPendingNetworks(null); setPendingToken(null); }}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 pt-2"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">ClickDash</h1>
          <p className="text-gray-500 mt-2">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 transition-colors"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>

          <div className="text-center pt-1">
            <a href="/forgot-password" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
              Forgot your password?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
